import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from './local-storage/local-storage.service';

/**
 * Анонимная сессия (§1.1). До регистрации человек ездит по UUID в заголовке
 * X-Anon-Id: по нему считаются четыре бесплатных вопроса, а метки точки и
 * сотрудника из QR цепляются к аккаунту в момент регистрации — по ним считается
 * выплата партнёру.
 *
 * После регистрации id больше не нужен: работает токен. Старая сессия помечается
 * занятой, и запросы по ней без токена получают 400 — поэтому id удаляем.
 */
@Injectable({ providedIn: 'root' })
export class AnonSessionService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly localStorage = inject(LocalStorageService);

  private static readonly KEY = 'pawprint_anon_id';

  readonly id = signal<string | null>(null);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.id.set(this.read());
  }

  /** Свой UUID генерируем сами, чтобы не ждать ответа бэка перед первым запросом. */
  public ensure(): string {
    const existing = this.id();
    if (existing) return existing;

    const fresh = this.uuid();
    this.store(fresh);
    return fresh;
  }

  /** Бэк мог выдать свой id — тогда живём с ним. */
  public adopt(anonId?: string | null): void {
    if (anonId && anonId !== this.id()) this.store(anonId);
  }

  /** После регистрации: чаты и метки уже переехали на аккаунт (§1.7). */
  public clear(): void {
    this.id.set(null);
    if (!isPlatformBrowser(this.platformId)) return;
    try { localStorage.removeItem(AnonSessionService.KEY); } catch {}
  }

  /** Есть ли токен: пока нет — работаем по анонимной сессии. */
  public get hasToken(): boolean {
    return !!this.localStorage.getAccessToken();
  }

  private read(): string | null {
    try { return localStorage.getItem(AnonSessionService.KEY); } catch { return null; }
  }

  private store(value: string): void {
    this.id.set(value);
    try { localStorage.setItem(AnonSessionService.KEY, value); } catch {}
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    // Запасной путь для старых браузеров и небезопасного контекста
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}
